import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import CourseViewPage from "@/pages/learning/CourseViewPage";
import PushPrimingSheet from "./PushPrimingSheet";
import { enablePush, getPushPermissionState } from "@/lib/push";

const PRIMED_KEY = "push_primed";
const NOTIF_KEY = "notifications_enabled";

async function prefGet(key: string): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key });
    return value;
  }
  return localStorage.getItem(key);
}
async function prefSet(key: string, value: string) {
  if (Capacitor.isNativePlatform()) {
    await Preferences.set({ key, value });
  } else {
    localStorage.setItem(key, value);
  }
}

/**
 * Mobile in-course view. Reuses the existing web CourseViewPage verbatim
 * (data fetching, players, progress, completion, resume). Adds:
 *   - Android hardware-back routing to /app/courses (native only)
 *   - First-course push priming sheet (P2)
 */
export default function CourseView() {
  const navigate = useNavigate();
  const [showPrime, setShowPrime] = useState(false);
  const [primeBusy, setPrimeBusy] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const subPromise = CapApp.addListener("backButton", () => {
      navigate("/app/courses");
    });
    return () => {
      subPromise.then((s) => s.remove()).catch(() => {});
    };
  }, [navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    (async () => {
      const primed = await prefGet(PRIMED_KEY);
      if (cancelled || primed === "true") return;
      const state = await getPushPermissionState();
      if (cancelled) return;
      // Only show when OS permission is still undecided (denied = user said no already)
      if (state !== "granted") {
        // checkPermissions returns 'granted' | 'denied' | 'prompt' – we only have
        // a granted/denied/unsupported wrapper. Re-check the raw state via prompt path
        // by querying once more through enablePush guard? Simpler: show the sheet only
        // when not granted AND not previously primed. If user previously denied at OS
        // level, tapping Enable will harmlessly no-op and we set primed=true.
        setShowPrime(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onEnable = async () => {
    setPrimeBusy(true);
    const result = await enablePush();
    if (result === "granted") {
      await prefSet(NOTIF_KEY, "true");
    }
    await prefSet(PRIMED_KEY, "true");
    setPrimeBusy(false);
    setShowPrime(false);
  };

  const onDismiss = async () => {
    await prefSet(PRIMED_KEY, "true");
    setShowPrime(false);
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <CourseViewPage />
      <PushPrimingSheet
        open={showPrime}
        busy={primeBusy}
        onEnable={onEnable}
        onDismiss={onDismiss}
      />
    </div>
  );
}
